/**
 * Shared in-memory cache for Dashboard V2 e-learning.
 * Strip + Hub share GetELearningModules so opening My E-Learning is instant.
 */

import requestAxios from '../../util/requestAxios';

const TTL_MS = 60_000;

/** API 404/400 before customize assessment — same empty shape V1 treats as "create program". */
export const EMPTY_ELEARNING_MODULES = {
    myBreakthroughExperience: [],
    myPersonalizedPath: [],
};

let cache = {
    userId: null,
    modules: null,
    courses: null,
    modulesFetchedAt: 0,
    coursesFetchedAt: 0,
};

let modulesInflight = null;
let modulesInflightUserId = null;
let coursesInflight = null;
let coursesInflightUserId = null;

function isNoProgramYetStatus(status) {
    return status === 404 || status === 400;
}

export function getCachedModules(userId) {
    if (!userId || cache.userId !== userId || !cache.modules) return null;
    return cache.modules;
}

export function getFreshCachedModules(userId) {
    if (!getCachedModules(userId)) return null;
    if (Date.now() - cache.modulesFetchedAt > TTL_MS) return null;
    return cache.modules;
}

export function setCachedModules(userId, modules) {
    if (!userId || !modules) return;
    cache = {
        ...cache,
        userId,
        modules,
        modulesFetchedAt: Date.now(),
    };
}

export function getCachedCourses(userId) {
    if (!userId || cache.userId !== userId || !cache.courses) return null;
    return cache.courses;
}

export function getFreshCachedCourses(userId) {
    if (!getCachedCourses(userId)) return null;
    if (Date.now() - cache.coursesFetchedAt > TTL_MS) return null;
    return cache.courses;
}

export function setCachedCourses(userId, courses) {
    if (!userId || !Array.isArray(courses)) return;
    cache = {
        ...cache,
        userId,
        courses,
        coursesFetchedAt: Date.now(),
    };
}

export function invalidateElearningCache(userId) {
    if (userId && cache.userId !== userId) return;
    cache = {
        userId: null,
        modules: null,
        courses: null,
        modulesFetchedAt: 0,
        coursesFetchedAt: 0,
    };
    modulesInflight = null;
    modulesInflightUserId = null;
    coursesInflight = null;
    coursesInflightUserId = null;
}

/**
 * Fetch core modules (Breakthrough + Path). Dedupes concurrent callers.
 * 404/400 (no assessment / no modules) → empty lists, not a hard error (V1 behavior).
 */
export async function loadElearningModules(
    userId,
    token,
    { force = false } = {}
) {
    if (!userId || !token) {
        return { error: true, data: null, status: '' };
    }
    if (!force) {
        const fresh = getFreshCachedModules(userId);
        if (fresh) return { error: false, data: fresh, status: 200 };
    }
    if (modulesInflight && modulesInflightUserId === userId && !force) {
        return modulesInflight;
    }

    const request = requestAxios({
        url: `Elearning/GetELearningModules/${userId}/1`,
        token,
    })
        .then((res) => {
            if (!res.error && res.data) {
                setCachedModules(userId, res.data);
                return { ...res, error: false };
            }
            if (isNoProgramYetStatus(res.status)) {
                setCachedModules(userId, EMPTY_ELEARNING_MODULES);
                return {
                    error: false,
                    data: EMPTY_ELEARNING_MODULES,
                    status: res.status,
                    noProgramYet: true,
                };
            }
            return res;
        })
        .finally(() => {
            if (modulesInflightUserId === userId) {
                modulesInflight = null;
                modulesInflightUserId = null;
            }
        });

    modulesInflight = request;
    modulesInflightUserId = userId;
    return request;
}

/**
 * Fetch purchased additional courses. Dedupes concurrent callers.
 */
export async function loadElearningCourses(
    userId,
    token,
    { force = false } = {}
) {
    if (!userId || !token) {
        return { error: true, data: null, status: '' };
    }
    if (!force) {
        const fresh = getFreshCachedCourses(userId);
        if (fresh) return { error: false, data: fresh, status: 200 };
    }
    if (coursesInflight && coursesInflightUserId === userId && !force) {
        return coursesInflight;
    }

    const request = requestAxios({
        url: 'AdditionalCourse/GetCourses',
        token,
    })
        .then((res) => {
            if (!res.error && Array.isArray(res.data)) {
                const purchased = res.data.filter((c) => c.purchased);
                setCachedCourses(userId, purchased);
                return { ...res, error: false, data: purchased };
            }
            return res;
        })
        .finally(() => {
            if (coursesInflightUserId === userId) {
                coursesInflight = null;
                coursesInflightUserId = null;
            }
        });

    coursesInflight = request;
    coursesInflightUserId = userId;
    return request;
}
